/**
 * @file FrameBox.tsx
 * @description Рамка кадра с фото, которое тянут пальцем или мышью
 * (phase19.16, партия SB120, этап 1; uiux-sb120-stage1 §2.4 «Кадр под
 * блок»). Файл не режется: рамка показывает место (пропорции блока на этом
 * экране), фото лежит в ней как на сайте (`object-fit: cover`, точка
 * фокуса, масштаб), перетаскивание двигает точку фокуса. Клавиатура —
 * стрелки по 5 %.
 */

import { useRef, type KeyboardEvent, type PointerEvent } from "react";

import { contentMediaUrl } from "../../../../lib/api-content-media";

interface Focus {
  x: number;
  y: number;
}

interface Props {
  mediaId: string;
  /** Пропорции рамки (ширина ÷ высота). */
  aspect: number;
  focus: Focus;
  zoom: number;
  onFocus: (focus: Focus) => void;
  label: string;
}

const clamp = (v: number) => Math.round(Math.min(100, Math.max(0, v)));

export function FrameBox({ mediaId, aspect, focus, zoom, onFocus, label }: Props) {
  const drag = useRef<{ x: number; y: number; focus: Focus } | null>(null);

  function onPointerDown(event: PointerEvent<HTMLDivElement>) {
    event.currentTarget.setPointerCapture?.(event.pointerId);
    drag.current = { x: event.clientX, y: event.clientY, focus };
  }
  function onPointerMove(event: PointerEvent<HTMLDivElement>) {
    const start = drag.current;
    const rect = event.currentTarget.getBoundingClientRect();
    if (!start || !rect.width || !rect.height) return;
    // Тянем фото вправо — в кадр входит левая часть: точка фокуса уходит влево.
    const dx = ((event.clientX - start.x) / rect.width) * 100;
    const dy = ((event.clientY - start.y) / rect.height) * 100;
    onFocus({ x: clamp(start.focus.x - dx), y: clamp(start.focus.y - dy) });
  }
  function stop() {
    drag.current = null;
  }
  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const step: Record<string, [number, number]> = { ArrowLeft: [-5, 0], ArrowRight: [5, 0], ArrowUp: [0, -5], ArrowDown: [0, 5] };
    const d = step[event.key];
    if (!d) return;
    event.preventDefault();
    onFocus({ x: clamp(focus.x + d[0]), y: clamp(focus.y + d[1]) });
  }
  const position = `${focus.x}% ${focus.y}%`;

  return (
    <div
      role="group"
      tabIndex={0}
      aria-label={`${label}: точка фокуса ${focus.x} % слева, ${focus.y} % сверху. Тяните фото или двигайте стрелками`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={stop}
      onPointerCancel={stop}
      onKeyDown={onKeyDown}
      className="relative mx-auto max-h-[50vh] w-full cursor-grab touch-none overflow-hidden rounded-xl bg-surface-low outline-none focus-visible:ring-2 focus-visible:ring-ring"
      style={{ aspectRatio: String(aspect), maxWidth: `calc(50vh * ${aspect})` }}
    >
      <img
        src={contentMediaUrl(mediaId)}
        alt=""
        draggable={false}
        className="pointer-events-none h-full w-full select-none object-cover"
        style={{ objectPosition: position, transform: `scale(${zoom})`, transformOrigin: position }}
      />
    </div>
  );
}
